import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { useComposition } from '@app/modules/compose/screens/view/hooks/useComposition';
import {
  NoPlatformsAlert,
  NoSourcesAlert,
  UploadsPanoramaLayout,
  publishMessages,
} from '@app/modules/social-export/publish';
import { useAvailableSocialExportPlatformsForStation } from '@app/modules/social-export/hooks/useAvailableSocialExportPlatformsForStation';
import { webApi } from '@mi-front/api-clients';
import { ComposeSocialExportUploadItem } from './ComposeSocialExportUploadItem';
import { useCompositionPublishableRenderedEdits } from './useCompositionPublishableRenderedEdits';

interface ComposeSocialExportPanoramaProps {
  readonly uploadsByPlatform: Map<string, webApi.CompositionSocialUploadItemDto[]> | undefined;
}

/**
 * Lists composition social uploads as cards; empty states when there are no sources or platforms.
 */
export function ComposeSocialExportPanorama({
  uploadsByPlatform,
}: ComposeSocialExportPanoramaProps) {
  const { composition } = useComposition();
  const { hasAvailableSources } = useCompositionPublishableRenderedEdits(composition.id);
  const { platforms: availablePlatforms, areConnectionsResolved } =
    useAvailableSocialExportPlatformsForStation(composition.stationId ?? undefined);
  const allUploads = Array.from(uploadsByPlatform?.values() ?? []).flat();

  return (
    <UploadsPanoramaLayout
      uploadCount={allUploads.length}
      hasAvailableSources={hasAvailableSources}
      areConnectionsResolved={areConnectionsResolved}
      hasConnectedPlatforms={availablePlatforms.length > 0}
      notPublishedMessage={publishMessages.panoramaNotPublishedComposition}
      noSourcesAlert={
        <NoSourcesAlert
          description={publishMessages.panoramaNoSourcesComposition}
          linkTo={composeRouteGenerator.edits(composition.id)}
        />
      }
      noPlatformsAlert={<NoPlatformsAlert />}
      uploadList={
        <>
          {allUploads.map((upload) => (
            <ComposeSocialExportUploadItem key={upload.id} upload={upload} />
          ))}
        </>
      }
    />
  );
}
